import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    FileText, CheckCircle2, XCircle, RefreshCcw, ExternalLink,
    ShieldAlert, Sparkles, ArrowRight,
} from "lucide-react";

/**
 * OCR Review Queue.
 *
 * Lists every statement whose `review_status == "pending_review"` — i.e.
 * the OCR pass extracted numbers, but finance hasn't signed off yet.
 * Each row exposes an in-line Approve (with optional corrections) or
 * Reject (with reason) action. No auto-posting anywhere — this is the
 * single choke point before OCR data flows into reports.
 */

const CONF_COLOR = {
    High: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Medium: "bg-amber-100 text-amber-700 border-amber-200",
    Low: "bg-rose-100 text-rose-700 border-rose-200",
};

const FIELD_GROUPS = [
    {
        title: "Dates",
        fields: [
            ["statement_date", "Statement Date", "date"],
            ["period_from", "Period From", "date"],
            ["period_to", "Period To", "date"],
            ["due_date", "Due Date", "date"],
        ],
    },
    {
        title: "Amounts",
        fields: [
            ["total_amount_due", "Total Due", "number"],
            ["min_amount_due", "Min Due", "number"],
            ["opening_balance", "Opening", "number"],
            ["closing_balance", "Closing", "number"],
            ["purchases", "Purchases", "number"],
            ["payments", "Payments", "number"],
            ["interest", "Interest", "number"],
            ["late_fee", "Late Fee", "number"],
            ["gst", "GST", "number"],
        ],
    },
];

function ConfidencePill({ level }) {
    if (!level) return null;
    const cls = CONF_COLOR[level] || CONF_COLOR.Low;
    return (
        <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
            data-testid={`conf-pill-${level.toLowerCase()}`}
        >
            {level}
        </span>
    );
}

function ReviewDialog({ item, onClose, onDone }) {
    const [values, setValues] = useState({});
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (item) {
            const seed = {};
            FIELD_GROUPS.forEach((g) => g.fields.forEach(([k]) => { seed[k] = item[k] ?? ""; }));
            setValues(seed);
            setRejectMode(false);
            setReason("");
        }
    }, [item]);

    if (!item) return null;
    const conf = item.ocr_confidence || {};

    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/statements/${item.id}/ocr-approve`, { corrections: values });
            toast.success("Statement approved — data now trusted in reports");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        if (reason.trim().length < 5) {
            toast.error("Please explain why (at least 5 characters).");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/statements/${item.id}/ocr-reject`, { reason: reason.trim() });
            toast.success("Rejected · OCR-derived amounts cleared. Enter values manually.");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="review-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-3">
                        Review OCR extraction
                        <span className="text-sm font-normal text-stone-500">
                            {item.bank_name} · •••• {item.last_4} · {item.statement_month}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-stone-500">
                        <Sparkles size={12} /> Gemini extraction · confidence heuristics per field
                    </span>
                    {item.file_url && (
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${item.file_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-auto text-[12px] text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"
                            data-testid="review-open-pdf"
                        >
                            Open source PDF <ExternalLink size={11} />
                        </a>
                    )}
                </div>

                {!rejectMode ? (
                    <div className="space-y-4">
                        {FIELD_GROUPS.map((g) => (
                            <section key={g.title}>
                                <div className="eyebrow mb-2">{g.title}</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {g.fields.map(([k, lbl, type]) => (
                                        <div key={k}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <Label className="eyebrow flex-1">{lbl}</Label>
                                                <ConfidencePill level={conf[k]} />
                                            </div>
                                            <Input
                                                type={type}
                                                value={values[k] ?? ""}
                                                onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))}
                                                className="mt-0.5"
                                                data-testid={`review-input-${k}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                        <section>
                            <div className="flex items-center gap-2">
                                <div className="eyebrow flex-1">Transactions</div>
                                <ConfidencePill level={conf.transactions} />
                                <span className="text-[12px] text-stone-500">
                                    {(item.transactions || []).length} rows extracted
                                </span>
                            </div>
                            <div className="text-[12px] text-stone-500 mt-1">
                                Approve to accept as-is · reject to clear numbers · edit each txn from the ledger page.
                            </div>
                        </section>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <Label className="eyebrow">Why are you rejecting this OCR pass?</Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={4}
                            placeholder="e.g. Numbers pulled from an old page — statement is a revision, will re-upload."
                            data-testid="reject-reason-input"
                        />
                        <div className="text-[12px] text-stone-500">
                            Rejecting clears the OCR-derived amounts and transactions. The PDF stays and you can
                            edit fields manually from the Statements list.
                        </div>
                    </div>
                )}

                <DialogFooter className="pt-4 flex-wrap gap-2">
                    {!rejectMode ? (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => setRejectMode(true)}
                                disabled={busy}
                                data-testid="review-reject-btn"
                            >
                                <XCircle size={14} className="mr-1.5" /> Reject
                            </Button>
                            <Button
                                onClick={approve}
                                disabled={busy}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="review-approve-btn"
                            >
                                <CheckCircle2 size={14} className="mr-1.5" />
                                {busy ? "Approving…" : "Approve extraction"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setRejectMode(false)} disabled={busy} data-testid="review-back-btn">
                                Back
                            </Button>
                            <Button
                                onClick={reject}
                                disabled={busy || reason.trim().length < 5}
                                className="bg-rose-600 hover:bg-rose-700 text-white"
                                data-testid="review-confirm-reject-btn"
                            >
                                {busy ? "Rejecting…" : "Confirm reject"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function StatementReview() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/statements/pending-review");
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const summary = (item) => {
        const conf = item.ocr_confidence || {};
        const levels = Object.values(conf);
        const low = levels.filter((v) => v === "Low").length;
        const med = levels.filter((v) => v === "Medium").length;
        const high = levels.filter((v) => v === "High").length;
        return { low, med, high };
    };

    return (
        <div className="max-w-[1200px] mx-auto" data-testid="ocr-review-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Statements · Review queue</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        OCR needs a human.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Nothing extracted here flows into reports until you approve it. Reject to wipe the OCR
                        numbers and enter values by hand.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-stone-200 bg-white text-sm"
                        data-testid="review-total-count"
                    >
                        <span className={`w-2 h-2 rounded-full ${rows.length > 0 ? "bg-amber-500" : "bg-emerald-500"}`} />
                        <span className="font-medium">{rows.length}</span>
                        <span className="text-stone-500">pending review</span>
                    </div>
                    <Button
                        onClick={load}
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        data-testid="review-refresh-btn"
                    >
                        <RefreshCcw size={13} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-stone-500">Loading review queue…</div>
            ) : rows.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
                    <ShieldAlert size={22} className="text-emerald-600 mx-auto mb-2" />
                    <div className="font-display text-2xl text-emerald-800">Queue is empty.</div>
                    <div className="text-sm text-emerald-700 mt-2">
                        Every uploaded statement has been reviewed. Upload a new PDF from the Statements page.
                    </div>
                    <Link
                        to="/statements"
                        className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-emerald-800 hover:text-emerald-900"
                    >
                        Go to Statements <ArrowRight size={13} />
                    </Link>
                </div>
            ) : (
                <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Card · month</th>
                                <th>Uploaded</th>
                                <th>Statement date</th>
                                <th className="text-right">Total due</th>
                                <th>Confidence</th>
                                <th>PDF</th>
                                <th className="text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const s = summary(r);
                                return (
                                    <tr key={r.id} data-testid={`review-row-${r.id}`}>
                                        <td>
                                            <div className="font-medium">{r.bank_name} · •••• {r.last_4}</div>
                                            <div className="text-[11px] text-stone-500 tabular-nums">{r.statement_month}</div>
                                        </td>
                                        <td className="text-xs tabular-nums">{fmtDate(r.upload_date)}</td>
                                        <td className="tabular-nums">{r.statement_date ? fmtDate(r.statement_date) : "—"}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(r.total_amount_due)}</td>
                                        <td>
                                            <div className="inline-flex items-center gap-1 text-[11px]">
                                                {s.high > 0 && <span className="text-emerald-700">{s.high} high</span>}
                                                {s.med > 0 && <span className="text-amber-700">{s.med} med</span>}
                                                {s.low > 0 && <span className="text-rose-700">{s.low} low</span>}
                                            </div>
                                        </td>
                                        <td>
                                            {r.file_url && (
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL}${r.file_url}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs text-stone-700 hover:text-stone-900"
                                                    data-testid={`row-pdf-${r.id}`}
                                                >
                                                    <FileText size={12} /> PDF
                                                </a>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => setActive(r)}
                                                className="bg-stone-900 hover:bg-stone-800 text-white"
                                                data-testid={`row-review-${r.id}`}
                                            >
                                                Review
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ReviewDialog
                item={active}
                onClose={() => setActive(null)}
                onDone={() => { setActive(null); load(); }}
            />
        </div>
    );
}
